import { Post } from "@/src/models/post.model";

/** Generic i18n key per visibility, used when no group name applies. */
export const VISIBILITY_LABEL_KEYS: Record<string, string> = {
  public: "LABEL_PUBLIC",
  private: "LABEL_PRIVATE",
  specific: "LABEL_SPECIFIC",
  group: "LABEL_GROUP",
};

/**
 * The groups a post is addressed to.
 *
 * A post composed inside a group carries it in `groupId`; one targeted at groups
 * from the main feed carries them in `visibleToGroupIds`. Both can be set (the
 * composer writes `visibleToGroupIds: [groupId]` for an in-group post), so
 * `groupId` leads and duplicates are dropped.
 */
export const postGroupIds = (
  post: Pick<Post, "groupId" | "visibleToGroupIds">,
): string[] => {
  const targeted = post.visibleToGroupIds ?? [];
  if (!post.groupId) return targeted.filter(Boolean);
  return [
    post.groupId,
    ...targeted.filter((id) => id && id !== post.groupId),
  ];
};

/**
 * Renders group names for the visibility chip: "Design", "Design, Marketing",
 * "Design, Marketing +2".
 *
 * Returns null when nothing resolved, so the caller can fall back to the plain
 * "Group" label rather than showing an empty chip — which happens for a group
 * the viewer is not a member of, e.g. editing a post aimed at a group the author
 * has since left.
 */
export const formatGroupAudience = (
  names: readonly string[],
  maxShown = 2,
): string | null => {
  const usable = names.filter(Boolean);
  if (usable.length === 0) return null;

  const shown = usable.slice(0, maxShown);
  const extra = usable.length - shown.length;
  return extra > 0 ? `${shown.join(", ")} +${extra}` : shown.join(", ");
};
